import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Link,
  useColorModeValue,
  useToast,
  Icon
} from "@chakra-ui/react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { authService } from "../../../services/authService";
import { getBusinessSlug } from "../../../utils/slugify";
import loginBg from "../../../assets/img/login-bg.png";
import menomV2Logo from "../../../assets/img/menomV2Logo.png";

function SignIn() {
  const navigate = useNavigate();
  const toast = useToast();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  // Chakra Color Mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.300");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");

  const handleClick = () => setShow(!show);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response = null;
      
      // Email formatına göre login tipini belirle
      const isStaffEmail = formData.email.includes('@staff.menomsoft.com');
      
      if (isStaffEmail) {
        // Staff email ise direkt staff login dene
        response = await authService.staffLogin({
          email: formData.email,
          password: formData.password
        });
      } else {
        // Business email ise önce business login dene
        try {
          response = await authService.login({
            email: formData.email,
            password: formData.password
          });
        } catch (businessError) {
          // Business login başarısız, staff login dene (fallback)
          try {
            response = await authService.staffLogin({
              email: formData.email,
              password: formData.password
            });
          } catch (staffError) {
            // Her iki login de başarısız
            throw businessError; // İlk hatayı göster
          }
        }
      }

      if (response && response.success) {
        toast({
          title: "Başarılı",
          description: "Giriş başarılı! Yönlendiriliyorsunuz...",
          status: "success",
          duration: 1500,
          isClosable: true
        });

        // Cookie'lerin set edildiğini doğrulamak için kısa bir bekleme
        // Cross-origin cookie'ler için browser'ın cookie'leri işlemesi gerekiyor
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Cookie'lerin çalıştığını doğrulamak için bir test API çağrısı yap
        try {
          const { default: api } = await import('../../../services/api');
          // Basit bir endpoint'e istek atarak cookie'lerin gönderildiğini doğrula
          await api.get('/api/business/restaurant/stats');
          console.log('✅ Cookie test başarılı - Cookie\'ler çalışıyor');
        } catch (error) {
          console.error('❌ Cookie test başarısız:', error);
          // Test başarısız olsa bile devam et (cookie'ler set edilmiş olabilir)
          // Kullanıcıyı bilgilendir
          toast({
            title: "Uyarı",
            description: "Oturum açılıyor, lütfen bekleyin...",
            status: "warning",
            duration: 2000,
            isClosable: true
          });
        }

        // Yönlendirme yap
          const businessSlug = getBusinessSlug();
          if (businessSlug) {
            navigate(`/admin/${businessSlug}/panel`);
          } else {
            navigate('/admin/panel');
          }
      }

    } catch (error) {
      console.error('Login error:', error);
      
      // Error objesi veya string olabilir
      const errorObj = typeof error === 'object' ? error : { message: error };
      const message = errorObj.message || error.message || "Giriş yapılırken bir hata oluştu";
      
      toast({
        title: "Hata",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Gradient Arka Plan */}
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex={0}
        bgGradient="linear(to-br, brand.100, purple.50)"
        opacity={0.5}
        pointerEvents="none"
      />

      {/* Logo - Sağ Üst */}
      <Box
        position="fixed"
        top={{ base: "20px", md: "30px" }}
        right={{ base: "20px", md: "40px" }}
        zIndex={2}
      >
        <img 
          src={menomV2Logo} 
          alt="Menom Logo" 
          style={{ 
            height: '50px',
            width: 'auto',
            objectFit: 'contain'
          }} 
        />
      </Box>

      <Flex
        direction="column"
        alignSelf="center"
        justifySelf="center"
        overflow="hidden"
        position="relative"
        zIndex={1}
      >
        <Flex
          direction="column"
          textAlign="center"
          justifyContent="center"
          align="center"
          mt="6.5rem"
          mb="30px"
        >
          <Heading color={textColor} fontSize="32px" mb="10px">
            Hoş Geldiniz!
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Hesabınıza giriş yapmak için email ve şifrenizi girin
          </Text>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          mb="60px"
          mt="20px"
          px={{ base: 4, sm: 6 }}
        >
          <Flex
            direction="column"
            w={{ base: "100%", sm: "440px", md: "520px" }}
            maxW={{ base: "520px", md: "560px" }}
            background="transparent"
            borderRadius="15px"
            p={{ base: "24px", sm: "32px", md: "40px" }}
            mx="auto"
            bg={useColorModeValue("white", "navy.800")}
            boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)"
            border="1px solid"
            borderColor={borderColor}
          >
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Email
              </FormLabel>
              <Input
                variant="auth"
                fontSize="sm"
                ms={{ base: "0px", md: "0px" }}
                type="email"
                placeholder="mail@sirket.com"
                mb="24px"
                fontWeight="500"
                size="lg"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Şifre
              </FormLabel>
              <InputGroup size="md">
                <Input
                  fontSize="sm"
                  ms={{ base: "0px", md: "0px" }}
                  type={show ? "text" : "password"}
                  placeholder="Min. 8 karakter"
                  mb="24px"
                  fontWeight="500"
                  size="lg"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={useColorModeValue("gray.400", "white")}
                    _hover={{ cursor: "pointer" }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <Button
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                w="100%"
                h="50"
                mb="24px"
                onClick={handleSubmit}
                isLoading={loading}
              >
                Giriş Yap
              </Button>

              <Flex justifyContent="flex-end" mb="16px">
                <Link
                  as={RouterLink}
                  to="/auth/forgot-password"
                  color="brand.500"
                  fontWeight="500"
                  fontSize="sm"
                  textDecoration="none"
                  _hover={{ textDecoration: "underline" }}
                >
                  Şifremi Unuttum
                </Link>
              </Flex>

              <Flex justifyContent="center" alignItems="center" mt="20px">
                <Text
                  color={textColorSecondary}
                  fontWeight="400"
                  fontSize="14px"
                >
                  Henüz hesabınız yok mu?{" "}
                  <Link
                    as={RouterLink}
                    to="/auth/sign-up"
                    color="brand.500"
                    fontWeight="500"
                    textDecoration="none"
                    _hover={{ textDecoration: "none" }}
                  >
                    Hemen Kaydolun
                  </Link>
                </Text>
              </Flex>
            </FormControl>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}

export default SignIn;